import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useAutomationTemplateQuery } from '../../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { styleSheet } from '../../../../components/automation/AutomationStepCard/styles';
import { AutomationPreview } from '../../../../components/automation/AutomationPreview';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { renderFilterCriteria } from '../../utils/filterDisplay';
import { switchStepStyles, getCaseBorderColor } from './styles/switchStepRendererStyles';

interface SwitchStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function SwitchStepRenderer({ nodeId: _nodeId, data, readOnly: readOnlyProp }: SwitchStepRendererProps) {
	const errorMessages = useErrorMessages();

	const { automationStep, automationTemplate } = data;
	const switchStep = automationStep as Api.ISwitchAutomationStep;
	const template = automationTemplate;

	const isDemoMode = !switchStep && !template;

	const canEdit = template?.status !== 'Archived' && !readOnlyProp;
	const readonly = !canEdit || isDemoMode || readOnlyProp;

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveSuccess: () => {
			// Query invalidation happens automatically
		},
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	if (!switchStep && !isDemoMode) {
		return <div>Loading...</div>;
	}

	const cases = switchStep?.cases || [];
	const CASE_NAME_DEFAULT = 'Default';
	const CASE_NAME_PREFIX = 'Case';

	return (
		<div className={css(switchStepStyles.container)}>
			{!isDemoMode && (
				<SaveStatus
					status={autoSave.saveStatus}
					variant='detailed'
					lastSavedAt={autoSave.lastSavedAt}
					errorMessage={autoSave.errorMessage}
					retryCount={autoSave.retryCount}
				/>
			)}
			<div className={css(styleSheet.fieldLabel)}>Conditional Branch</div>
			<div className={css(switchStepStyles.infoSection)}>
				<div className={css(switchStepStyles.infoText)}>
					This step creates different paths based on conditions. Each case below represents a branch.
				</div>
				{cases.length === 0 ? (
					<div className={css(switchStepStyles.emptyCasesText)}>
						No cases defined. Add cases to create conditional branches.
					</div>
				) : (
					<div className={css(switchStepStyles.casesContainer)}>
						{cases.map((caseStatement: Api.IAutomationStepCaseStatement, index: number) => {
							const caseTemplateId = caseStatement.automationTemplateId;
							const isDefault = caseStatement.isDefault;
							// Use case name if provided, otherwise use consistent numbering
							const caseName = isDefault ? CASE_NAME_DEFAULT : caseStatement.name || `${CASE_NAME_PREFIX} ${index + 1}`;

							return (
								<CaseBranch
									key={caseStatement.id || `case-${index}`}
									caseStatement={caseStatement}
									caseName={caseName}
									caseTemplateId={caseTemplateId}
									isDefault={isDefault}
									readonly={readonly}
									caseIndex={index}
								/>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

interface CaseBranchProps {
	caseStatement: Api.IAutomationStepCaseStatement;
	caseName: string;
	caseTemplateId?: string;
	isDefault: boolean;
	readonly: boolean;
	caseIndex: number;
}

function CaseBranch({
	caseStatement,
	caseName,
	caseTemplateId,
	isDefault,
	readonly: _readonly,
	caseIndex,
}: CaseBranchProps) {
	const { data: childTemplate, isLoading } = useAutomationTemplateQuery({
		id: caseTemplateId,
		enabled: !!caseTemplateId,
	});

	const filter = caseStatement.filter;
	const borderColor = getCaseBorderColor(caseIndex, isDefault);
	const hasFilter = !isDefault && !!filter;

	// Calculate child node count from template
	const childNodeCount = React.useMemo(() => {
		if (!childTemplate) {
			return 0;
		}
		const steps = childTemplate.draftSteps || childTemplate.publishedSteps;
		return steps?.length || 0;
	}, [childTemplate]);

	return (
		<div className={css(switchStepStyles.caseCard)} style={{ borderLeftColor: borderColor }}>
			<div className={css(switchStepStyles.caseHeader)}>
				<span
					className={css(
						switchStepStyles.caseBadge,
						isDefault ? switchStepStyles.caseBadgeDefault : switchStepStyles.caseBadgeNormal
					)}
					style={{ backgroundColor: borderColor }}
				>
					{isDefault ? 'DEFAULT' : `CASE ${caseIndex + 1}`}
				</span>
				<span className={css(switchStepStyles.caseName)}>{caseName}</span>
				{childNodeCount > 0 && (
					<span className={css(switchStepStyles.caseNodeCount)} title={`${childNodeCount} steps in this branch`}>
						{childNodeCount} {childNodeCount === 1 ? 'step' : 'steps'}
					</span>
				)}
			</div>

			{/* Render filter condition if present */}
			{hasFilter && renderFilterCriteria(filter)}

			{isLoading ? (
				<div
					className={css(hasFilter ? switchStepStyles.loadingContainerWithFilter : switchStepStyles.loadingContainer)}
				>
					<LoadingSpinner type='small' />
				</div>
			) : childTemplate ? (
				<div
					className={css(
						hasFilter ? switchStepStyles.templatePreviewContainerWithFilter : switchStepStyles.templatePreviewContainer
					)}
				>
					<AutomationPreview
						template={childTemplate as unknown as Api.AutomationTemplateViewModel}
						showPublished={false}
					/>
				</div>
			) : (
				<div className={css(hasFilter ? switchStepStyles.noTemplateTextWithFilter : switchStepStyles.noTemplateText)}>
					No template assigned to this case.
				</div>
			)}
		</div>
	);
}
